import { getConfig } from '@/config/runtimeConfig';
import { assertOrgId } from '@/types/orgId';

import { BaseApiClient } from './baseApiClient';

import type {
  CreateCheckoutSessionResponse,
  GiftCardRedemptionResponse,
  TokenBalance,
  TokenTransaction,
  TransactionFilters,
  TransactionPagination,
  TransactionSort,
} from '@gw/ui/types';

class OrgTokenBalanceClient extends BaseApiClient {
  constructor() {
    // GW-6394: Use baseURLFactory to defer getConfig() until first API call.
    super({ baseURLFactory: () => getConfig().tokensApiBaseUrl ?? getConfig().apiBaseUrl });
  }

  // The gw-smart-tokens API returns { available, reserved, total } directly —
  // no field-mapping adapter needed. See balanceResponse in:
  // gw-smart-tokens/functions/smart-tokens-balance/main.go
  getBalance(orgId: string): Promise<TokenBalance> {
    assertOrgId(orgId);
    return this.get<TokenBalance>(`/v1/organizations/${orgId}/tokens/balance`);
  }
}

const orgTokenBalanceClient = new OrgTokenBalanceClient();

class SmartTokensOrgClient extends BaseApiClient {
  constructor() {
    // GW-6394: Use baseURLFactory to defer getConfig() until first API call.
    super({ baseURLFactory: () => getConfig().tokensApiBaseUrl ?? getConfig().apiBaseUrl });
  }

  getTransactions(
    orgId: string,
    params?: {
      limit?: number;
      offset?: number;
      sortBy?: TransactionSort['field'];
      sortOrder?: TransactionSort['order'];
      types?: TransactionFilters['types'];
      statuses?: TransactionFilters['statuses'];
      applicationIds?: string[];
      userIds?: string[];
      startDate?: string;
      endDate?: string;
      search?: string;
    }
  ): Promise<{ transactions: TokenTransaction[]; pagination: TransactionPagination }> {
    assertOrgId(orgId);
    const query = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            query.set(key, value.join(','));
          } else {
            query.set(key, String(value));
          }
        }
      });
    }

    const queryString = query.toString();
    const path = `/v1/organizations/${orgId}/transactions${queryString ? `?${queryString}` : ''}`;

    return this.get(path);
  }

  createCheckout(request: {
    organizationId: string;
    packageId?: string;
    customAmount?: number;
    type: 'one_time';
    successUrl?: string;
    cancelUrl?: string;
  }): Promise<CreateCheckoutSessionResponse> {
    const { organizationId, ...body } = request;
    assertOrgId(organizationId);
    return this.post<CreateCheckoutSessionResponse>(
      `/v1/organizations/${organizationId}/tokens/purchase`,
      body
    );
  }
}

const smartTokensOrgClient = new SmartTokensOrgClient();

/**
 * Payments API client class
 * Manages Stripe checkout session creation and payment processing
 */
class PaymentsApiClient extends BaseApiClient {
  // ============================================================================
  // Organization-Level Token Management (for Org Admins)
  // ============================================================================

  /**
   * Get token balance for a specific organization
   *
   * HTTP Method: GET /organizations/:orgId/tokens/balance
   * Authorization: Required (JWT token)
   * RBAC: org_admin or global_admin only
   *
   * @param orgId - Organization ID
   * @returns Token balance for the organization
   * @throws {ApiError} When API request fails or insufficient permissions (403)
   */
  async getOrganizationTokenBalance(orgId: string): Promise<TokenBalance> {
    return orgTokenBalanceClient.getBalance(orgId);
  }

  /**
   * Get transaction history for a specific organization (PRP-023)
   *
   * HTTP Method: GET /organizations/:orgId/transactions
   * Authorization: Required (JWT token)
   * RBAC: Any authenticated organization member
   *
   * Supports advanced filtering, pagination, sorting, and search.
   *
   * @param orgId - Organization ID
   * @param params - Query parameters for filtering, pagination, and sorting
   * @returns Transactions with pagination metadata
   * @throws {ApiError} When API request fails or insufficient permissions (403)
   */
  async getOrganizationTransactions(
    orgId: string,
    params?: {
      limit?: number;
      offset?: number;
      sortBy?: TransactionSort['field'];
      sortOrder?: TransactionSort['order'];
      types?: TransactionFilters['types'];
      statuses?: TransactionFilters['statuses'];
      applicationIds?: string[];
      userIds?: string[];
      startDate?: string;
      endDate?: string;
      search?: string;
    }
  ): Promise<{
    transactions: TokenTransaction[];
    pagination: TransactionPagination;
  }> {
    return smartTokensOrgClient.getTransactions(orgId, params);
  }

  /**
   * Create organization-level Stripe checkout session
   *
   * HTTP Method: POST /organizations/:orgId/tokens/purchase
   * Authorization: Required (JWT token)
   * RBAC: org_admin or global_admin only
   *
   * @param request - Organization checkout session parameters
   * @returns Checkout session with Stripe redirect URL
   * @throws {ApiError} When API request fails or insufficient permissions (403)
   */
  async createOrganizationCheckout(request: {
    organizationId: string;
    packageId?: string;
    customAmount?: number;
    type: 'one_time';
    successUrl?: string;
    cancelUrl?: string;
  }): Promise<CreateCheckoutSessionResponse> {
    return smartTokensOrgClient.createCheckout(request);
  }

  // ============================================================================
  // Gift Card Redemption
  // ============================================================================

  /**
   * Redeem a gift card for an organization
   *
   * HTTP Method: POST /organizations/:orgId/tokens/redeem-gift-card
   * Authorization: Required (JWT token)
   * RBAC: org_admin or global_admin only
   *
   * @param orgId - Organization ID
   * @param code - Gift card code to redeem
   * @returns Gift card redemption result with amount credited
   * @throws {ApiError} When API request fails:
   *   - 400: Invalid gift card code
   *   - 403: Access denied (insufficient permissions)
   *   - 404: Gift card not found
   *   - 410: Gift card already redeemed or expired
   */
  async redeemGiftCard(orgId: string, code: string): Promise<GiftCardRedemptionResponse> {
    assertOrgId(orgId);
    return this.post<GiftCardRedemptionResponse>(
      `/organizations/${orgId}/tokens/redeem-gift-card`,
      { code }
    );
  }
}

/**
 * Singleton instance export
 */
export const paymentsApi = new PaymentsApiClient();
